import { useQuery } from '@tanstack/react-query'
import { Edit, Eye, RefreshCw, SlidersHorizontal } from 'lucide-react'
import { useState } from 'react'

import { PriceAdjustment, purchasesService } from '../../services/purchases.service'
import { supplierService } from '../../services/supplier.service'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { DiscreteLoader } from '../ui/discrete-loader'
import { GlobalPaginator } from '../ui/global-paginator'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { TabsContent } from '../ui/tabs'
import { GlobalFilterBar } from '../ui/GlobalFilterBar'

interface PriceAdjustmentsTabProps {
  openPriceAdjustmentModal: (adjustment: PriceAdjustment) => void
  refreshKey?: number
}

const perPage = 10

export function PriceAdjustmentsTab({ openPriceAdjustmentModal, refreshKey = 0 }: PriceAdjustmentsTabProps) {
  const [supplierId, setSupplierId] = useState('all')
  const [status, setStatus] = useState('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)

  const adjustmentsQuery = useQuery({
    queryKey: ['purchase-price-adjustments', { supplierId, status, dateFrom, dateTo, page, refreshKey }],
    queryFn: () => purchasesService.getPriceAdjustments({
      supplier_id: supplierId === 'all' ? undefined : Number(supplierId),
      status: status === 'all' ? undefined : status as 'pending' | 'updated',
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      page,
      per_page: perPage,
    }),
  })
  const suppliersQuery = useQuery({
    queryKey: ['suppliers', 'price-adjustment-filter'],
    queryFn: () => supplierService.getItems({ all: true, is_active: true }),
  })

  const paginator = adjustmentsQuery.data?.data
  const adjustments = paginator?.data ?? []
  const suppliers = suppliersQuery.data
    ? (Array.isArray(suppliersQuery.data.data) ? suppliersQuery.data.data : suppliersQuery.data.data.data)
    : []
  const resetPage = () => setPage(1)

  return (
    <TabsContent value="adjustments" className="space-y-4">
        <GlobalFilterBar
  show={[]}
  values={{}}
  onChange={() => {}}
>
  <div className="flex w-full flex-nowrap items-center gap-3">
    <Select
      value={supplierId}
      onValueChange={(value) => {
        setSupplierId(value)
        resetPage()
      }}
    >
      <SelectTrigger className="h-10 w-[220px] shrink-0">
        <SelectValue placeholder="Todos los proveedores" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los proveedores</SelectItem>
        {suppliers.map((supplier) => (
          <SelectItem key={supplier.id} value={String(supplier.id)}>
            {supplier.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>

    <Select
      value={status}
      onValueChange={(value) => {
        setStatus(value)
        resetPage()
      }}
    >
      <SelectTrigger className="h-10 w-[200px] shrink-0">
        <SelectValue placeholder="Todos los estados" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los estados</SelectItem>
        <SelectItem value="pending">Pendiente</SelectItem>
        <SelectItem value="updated">Revisado</SelectItem>
      </SelectContent>
    </Select>

    <Input
      type="date"
      value={dateFrom}
      onChange={(event) => {
        setDateFrom(event.target.value)
        resetPage()
      }}
      className="h-10 w-[180px] shrink-0"
      aria-label="Fecha desde"
    />

    <Input
      type="date"
      value={dateTo}
      onChange={(event) => {
        setDateTo(event.target.value)
        resetPage()
      }}
      className="h-10 w-[180px] shrink-0"
      aria-label="Fecha hasta"
    />
  </div>
</GlobalFilterBar>

          

      <div className="flex items-center justify-between">
        <p className="text-sm text-header-foreground">{paginator?.total ?? 0} ajustes encontrados</p>
      </div>

      {adjustmentsQuery.isLoading ? (
        <div className="min-h-64 rounded-lg border border-table-border bg-table-bg"><DiscreteLoader text="Cargando ajustes de precio..." /></div>
      ) : adjustmentsQuery.isError ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="font-medium text-red-800">No fue posible cargar los ajustes de precio</p>
          <Button variant="outline" className="mt-4" onClick={() => void adjustmentsQuery.refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
          </Button>
        </div>
      ) : adjustments.length === 0 ? (
        <div className="flex min-h-64 flex-col items-center justify-center rounded-lg border border-dashed border-table-border bg-table-bg p-6 text-center">
          <SlidersHorizontal className="mb-3 h-10 w-10 text-card-description" />
          <p className="font-medium text-table-foreground">No se encontraron ajustes de precio</p>
          <p className="mt-1 text-sm text-card-description">Los ingresos con productos existentes y costo nuevo aparecerán aquí.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-table-border bg-table-bg">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1050px]">
              <thead className="border-b border-table-border bg-table-header-bg">
                <tr>
                  {['ID', 'Proveedor', 'Fecha', 'ID Compra', 'Productos', 'Estado', 'Fecha Modificación', 'Acciones'].map((heading) => (
                    <th key={heading} className="px-6 py-3 text-left text-xs font-medium uppercase text-table-header-foreground">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-table-border">
                {adjustments.map((adjustment) => (
                  <tr key={adjustment.id} className="text-table-foreground transition-colors hover:bg-table-row-hover">
                    <td className="px-6 py-4 font-medium text-table-foreground">{adjustment.id}</td>
                    <td className="px-6 py-4 text-table-foreground">{adjustment.supplier}</td>
                    <td className="px-6 py-4 text-table-foreground">{adjustment.date}</td>
                    <td className="px-6 py-4 text-table-foreground">{adjustment.purchaseId}</td>
                    <td className="px-6 py-4 text-table-foreground">{adjustment.items} productos</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Badge className={adjustment.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}>
                          {adjustment.status === 'pending' ? 'Pendiente' : 'Revisado'}
                        </Badge>
                        {adjustment.isEdited && <Badge className="bg-purple-100 text-purple-700">Editado</Badge>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-table-foreground">{adjustment.editDate || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openPriceAdjustmentModal(adjustment)} className="rounded p-1" title={adjustment.status === 'pending' ? 'Ver detalles' : 'Ver/Editar'}>
                          <Eye className="h-4 w-4 text-card-foreground transition-colors group-hover:text-primary" />
                        </button>
                        {adjustment.status === 'updated' && (
                          <button type="button" onClick={() => openPriceAdjustmentModal(adjustment)} className="rounded p-1 hover:bg-slate-100" title="Editar">
                            <Edit className="h-4 w-4 text-primary" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <GlobalPaginator
            totalItems={paginator?.total ?? 0}
            itemsPerPage={paginator?.per_page ?? perPage}
            currentPage={paginator?.current_page ?? page}
            from={paginator?.from ?? 0}
            to={paginator?.to ?? 0}
            onPageChange={setPage}
          />
        </div>
      )}
    </TabsContent>
  )
}
